import moment from 'moment-timezone';
import { generateClient } from 'aws-amplify/api';
import { logApiEvent } from 'src/logger';
import { getTimeDifference } from 'src/utilities/DateTimeUtilities';

// Utility to format error messages
export const formatApiErrors = (errors: any[]): string => {
  return 'API responded with errors: ' + errors.map((error) => error.message).join(', ');
};

interface GraphQLRequestOptions {
  query: any;
  variables: any;
  requestName: string;
  requestType: 'query' | 'mutation';
  authMode?: 'apiKey' | 'iam' | 'userPool' | 'oidc' | 'lambda';
  // Optional per-request override of the Authorization header (e.g. send the
  // Cognito ID token instead of the default access token, so resolvers can read
  // custom: claims like custom:LDAP_GROUPS). Only affects this single request.
  authToken?: string;
  // Cancels the request, Amplify's retries included, when it fires; the call then rejects with an AbortError.
  signal?: AbortSignal;
}

const cancelledError = (): Error => {
  const error = new Error('Request was cancelled');
  error.name = 'AbortError';
  return error;
};

export const fetchGraphQLRequest = async <T>({
  query,
  variables,
  requestName,
  requestType,
  authMode,
  authToken,
  signal
}: GraphQLRequestOptions): Promise<T> => {
  if (signal?.aborted) throw cancelledError();
  const initiateTimer = moment();
  let response: any;
  let elapsed: number;
  const client = generateClient();
  let cancelRequest: (() => void) | undefined;

  try {
    const request = client.graphql({
      query: query,
      variables: variables,
      ...(authMode && { authMode }),
      ...(authToken && { authToken })
    });
    // Queries and mutations are promises; only a subscription would be an Observable, and this helper sends none.
    if (request instanceof Promise) {
      cancelRequest = () => {
        client.cancel(request);
      };
      signal?.addEventListener('abort', cancelRequest, { once: true });
    }
    response = await request;
    elapsed = getTimeDifference(initiateTimer);

    if (response.errors && response.errors.length > 0) {
      const errorMessage = formatApiErrors(response.errors);
      logApiEvent(
        {
          requestType: requestType,
          requestName: requestName,
          requestStatus: 'API_FAILED',
          responseTime: elapsed
        },
        response.errors
      );
      throw new Error(errorMessage);
    }

    logApiEvent({
      requestType: requestType,
      requestName: requestName,
      requestStatus: 'API_SUCCESS',
      responseTime: elapsed
    });

    return response.data;
  } catch (error: any) {
    // A cancelled request is the caller's choice, not an API failure, so it is not logged as one.
    if (signal?.aborted || client.isCancelError(error)) throw cancelledError();
    elapsed = getTimeDifference(initiateTimer);
    const errorMessage = error.errors ? formatApiErrors(error.errors) : 'API Request Failed: Unexpected error occurred';
    logApiEvent(
      {
        requestType: requestType,
        requestName: requestName,
        requestStatus: 'API_FAILED',
        responseTime: elapsed
      },
      { errorMessage }
    );

    throw new Error(errorMessage);
  } finally {
    if (cancelRequest) signal?.removeEventListener('abort', cancelRequest);
  }
};
